import React, { useState } from "react";
import FormModal from "../forms/FormModal";
import { exportWorkbook, shapeExport } from "../../utils/tableExport";
import { errorMessage } from "../../utils/errorMessage";

const FORMATS = [{ value: "xlsx", label: "Excel (.xlsx)" }, { value: "csv", label: "CSV (.csv)" }];

// Sort by / Order / Format selects for the project and lead export forms, which keep their server-side filters.
export const sortAndFormatFields = (form, setForm, sortOptions) => {
  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
  return [
    { label: "Sort by (optional)", type: "select", value: form.sortBy, onChange: set("sortBy"), options: [{ value: "", label: "Default order" }, ...sortOptions] },
    ...(form.sortBy ? [{ label: "Order", type: "select", value: form.sortDir, onChange: set("sortDir"), options: [{ value: "asc", label: "Ascending" }, { value: "desc", label: "Descending" }] }] : []),
    { label: "Format", type: "select", value: form.format, onChange: set("format"), options: FORMATS },
  ];
};

// The one export dialog every table uses. `scopes` are the row sets on offer ({ value, label, rows }),
// `columns` every exportable column ({ key, label, value, visible }) and `groups` the ways the page can group
// ({ value, label, get }); the "selected" scope adds -selected to the file name. Without `columns` it only asks for the
// format and hands it to `onExport`.
export default function ExportDialog({
  title, filename, scopes = [], defaultScope, columns = null, groups = [], defaultGroup = "",
  extraSheets = null, onExport = null, onExported = () => {}, onClose,
}) {
  const [scope, setScope] = useState(defaultScope || scopes[0]?.value);
  const [columnSet, setColumnSet] = useState("visible");
  const [sortKey, setSortKey] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  const [group, setGroup] = useState(defaultGroup);
  const [format, setFormat] = useState("xlsx");
  const [error, setError] = useState("");

  const chosen = columns && columns.filter((c) => columnSet === "all" || c.visible);
  const hasHidden = columns && columns.some((c) => !c.visible);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (!columns) {
        await onExport({ format });
        return onClose();
      }
      const rows = scopes.find((s) => s.value === scope)?.rows || [];
      const shaped = shapeExport({ rows, columns: chosen, sort: sortKey ? { key: sortKey, dir: sortDir } : null, group: groups.find((g) => g.value === group) });
      const extra = typeof extraSheets === "function" ? await extraSheets() : extraSheets || [];
      const file = scope === "selected" ? `${filename}-selected` : filename;
      const kind = await exportWorkbook({ sheets: [{ name: filename, ...shaped }, ...extra], filename: file, format });
      onExported(shaped.rows.length, kind);
      onClose();
    } catch (err) {
      setError(errorMessage(err, "Failed to export."));
    }
  };

  const field = (label, value, onChange, options) => ({ label, type: "select", value, options, onChange: (e) => onChange(e.target.value) });
  const fields = [
    ...(scopes.length > 1 ? [field("Rows", scope, setScope, scopes.map((s) => ({ value: s.value, label: `${s.label} (${s.rows.length})` })))] : []),
    ...(hasHidden ? [field("Columns", columnSet, setColumnSet, [
      { value: "visible", label: `Visible columns (${columns.filter((c) => c.visible).length})` },
      { value: "all", label: `All columns (${columns.length})` },
    ])] : []),
    ...(chosen ? [field("Sort by", sortKey, setSortKey, [{ value: "", label: "As shown on screen" }, ...chosen.map((c) => ({ value: c.key, label: c.label }))])] : []),
    ...(sortKey ? [field("Order", sortDir, setSortDir, [{ value: "asc", label: "Ascending" }, { value: "desc", label: "Descending" }])] : []),
    ...(groups.length ? [field("Group by", group, setGroup, [{ value: "", label: "No grouping" }, ...groups.map((g) => ({ value: g.value, label: g.label }))])] : []),
    field("Format", format, setFormat, FORMATS),
    ...((extraSheets || !columns) && format === "csv" ? [{ type: "custom", render: () => <p style={{ margin: 0, fontSize: "0.8rem", color: "#64748b" }}>CSV holds one table, so only the first sheet is saved.</p> }] : []),
  ];

  return <FormModal title={title} fields={fields} error={error} onSubmit={submit} onCancel={onClose} submitLabel="Export" />;
}
